/**
 * MeshService — main-thread worker pool that turns SharkGenomes into ready-to-render
 * THREE.BufferGeometry without ever blocking the render loop.
 */

import * as THREE from 'three';
import { VOXEL } from '../data/tuning.ts';
import type { SharkGenome } from './Genome.ts';
import type { SharkSpec } from './SharkForge.ts';
import type { MeshRequest, MeshResponse, MeshError, JawRig } from './worker/meshWorker.ts';

export type { MeshRequest, MeshResponse, MeshError, JawRig };

/** TigerSculpt's model-space x extent (MX1 - MX0); keep in step with that file. */
const TIGER_MODEL_SPAN = 1.24;

export interface SharkMesh {
  geometry: THREE.BufferGeometry; // non-indexed; attributes: position, normal, color, spineT
  /** present only for hand-tuned models with an articulated jaw (the tiger) */
  jaw: JawRig | null;
  triangleCount: number;
  solidVoxels: number;
  size: THREE.Vector3;
  surface: { offsets: Float32Array; colors: Float32Array; count: number };
  /** the roster genome this was built from, or null when it came from a player's spec */
  genome: SharkGenome | null;
  /** the player's spec this was built from, or null when it came from the roster */
  spec: SharkSpec | null;
  buildMs: number; // wall clock incl. queue
  sculptMs: number;
  meshMs: number;
}

/**
 * The swim shader displaces the tail laterally by up to ~SWIM.ampCruise and yaws the slab on
 * top of that, so the meshed bounding sphere is a lie by roughly half a body width. Three
 * culls on that sphere: without the inflation, sharks blink out at the screen edge exactly
 * when they are thrashing hardest. 1.6 covers the worst case (lunge amp spike) with margin.
 */
const SWIM_CULL_INFLATE = 1.6;

/** A worker that fails twice is broken, not unlucky — stop respawning and report it. */
const MAX_WORKER_RESTARTS = 2;

type WorkerMessage = MeshResponse | MeshError;

interface WorkerSlot {
  index: number;
  worker: Worker;
  /** job ids dispatched to this worker and not yet answered — the load metric for dispatch */
  inFlight: Set<number>;
  restarts: number;
  dead: boolean;
}

interface PendingJob {
  resolve: (mesh: SharkMesh) => void;
  reject: (err: Error) => void;
  startedAt: number;
  genome: SharkGenome | null;
  spec: SharkSpec | null;
}

function buildGeometry(msg: MeshResponse): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  // Views over the transferred buffers — zero copy, the worker no longer owns this memory.
  geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(msg.positions), 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(msg.normals), 3));
  geometry.setAttribute('color', new THREE.BufferAttribute(new Float32Array(msg.colors), 3));
  geometry.setAttribute('spineT', new THREE.BufferAttribute(new Float32Array(msg.spineT), 1));
  if (msg.jawGroup !== null) {
    geometry.setAttribute('jawGroup', new THREE.BufferAttribute(new Float32Array(msg.jawGroup), 1));
  }

  geometry.computeBoundingSphere();
  if (geometry.boundingSphere) geometry.boundingSphere.radius *= SWIM_CULL_INFLATE;

  // GreedyMesh recentres the origin on the solid AABB in all three axes, so the box is symmetric.
  const hx = msg.size[0] * 0.5;
  const hy = msg.size[1] * 0.5;
  const hz = msg.size[2] * 0.5;
  geometry.boundingBox = new THREE.Box3(
    new THREE.Vector3(-hx, -hy, -hz),
    new THREE.Vector3(hx, hy, hz),
  );

  return geometry;
}

export class MeshService {
  private readonly slots: WorkerSlot[] = [];
  private readonly jobs = new Map<number, PendingJob>();
  private nextJobId = 1;
  private disposed = false;

  constructor(workerCount?: number) {
    let count: number;
    if (workerCount !== undefined) {
      count = Math.max(1, Math.floor(workerCount));
    } else {
      // Leave a core for the render thread; below 2 the lab stalls on a 10-shark burst,
      // above 4 the workers just fight each other for the same memory bandwidth.
      const hw = typeof navigator !== 'undefined' && navigator.hardwareConcurrency
        ? navigator.hardwareConcurrency
        : 4;
      count = Math.min(4, Math.max(2, hw - 1));
    }
    for (let i = 0; i < count; i++) this.slots.push(this.spawn(i));
  }

  /** number of jobs queued or in flight */
  get pending(): number {
    return this.jobs.size;
  }

  /** A player's own shark, from the generator. Same pool, same off-thread build. */
  buildSpec(spec: SharkSpec): Promise<SharkMesh> {
    // voxelSize is computed inside the forge from the species' natural length, so the value
    // sent here is only a placeholder the worker overwrites.
    return this.dispatch(null, spec, 1);
  }

  build(genome: SharkGenome): Promise<SharkMesh> {
    return this.dispatch(
      genome, null,
      // genome.scale is defined as a post-voxelisation world scale, and voxel size is the only
      // world-space knob in the pipeline — folding it in here keeps mesh.size honest for free.
      // The tiger is authored in its own normalised model space at a much finer grid, so it
      // sizes from a target world length instead of the roster's shared voxel size. MODEL_SPAN
      // is the sculptor's x extent (MX1 - MX0).
      genome.model === 'tiger'
        ? (genome.tigerLength ?? 8.2) / ((genome.tigerRes ?? 200) * TIGER_MODEL_SPAN)
        : VOXEL.size * (genome.scale > 0 ? genome.scale : 1),
    );
  }

  private dispatch(
    genome: SharkGenome | null, spec: SharkSpec | null, voxelSize: number,
  ): Promise<SharkMesh> {
    if (this.disposed) return Promise.reject(new Error('MeshService: build() after dispose()'));

    const slot = this.leastLoaded();
    if (!slot) {
      return Promise.reject(new Error(
        'MeshService: every mesh worker failed to start — check the console for the worker module error',
      ));
    }

    const jobId = this.nextJobId++;
    const req: MeshRequest = {
      type: 'build',
      jobId,
      voxelSize,
      maxSurfaceVoxels: VOXEL.maxShatterVoxels,
      genome,
      spec,
    };

    return new Promise<SharkMesh>((resolve, reject) => {
      this.jobs.set(jobId, { resolve, reject, startedAt: performance.now(), genome, spec });
      slot.inFlight.add(jobId);
      try {
        slot.worker.postMessage(req);
      } catch (cause) {
        // A non-cloneable genome throws synchronously; without this the promise never settles.
        this.jobs.delete(jobId);
        slot.inFlight.delete(jobId);
        reject(new Error(
          `MeshService: job ${jobId} could not be sent to the worker — ` +
          `a genome or spec must be plain structured-cloneable data (${String(cause)})`,
        ));
      }
    });
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    const err = new Error('MeshService: disposed while jobs were still in flight');
    for (const job of this.jobs.values()) job.reject(err);
    this.jobs.clear();

    for (const slot of this.slots) {
      slot.dead = true;
      slot.worker.terminate();
    }
    this.slots.length = 0;
  }

  private spawn(index: number): WorkerSlot {
    // This exact `new URL(..., import.meta.url)` form is what Vite pattern-matches to emit
    // the worker chunk. Do not hoist the URL into a variable.
    const worker = new Worker(new URL('./worker/meshWorker.ts', import.meta.url), { type: 'module' });
    const slot: WorkerSlot = { index, worker, inFlight: new Set(), restarts: 0, dead: false };

    worker.addEventListener('message', (ev) => {
      this.onMessage(slot, ev.data as WorkerMessage);
    });
    worker.addEventListener('error', (ev) => {
      const where = ev.filename ? ` (${ev.filename}:${ev.lineno}:${ev.colno})` : '';
      this.killSlot(slot, `mesh worker #${index} crashed: ${ev.message || 'unknown error'}${where}`);
    });
    worker.addEventListener('messageerror', () => {
      this.killSlot(slot, `mesh worker #${index} sent a message that could not be deserialised`);
    });

    return slot;
  }

  private leastLoaded(): WorkerSlot | null {
    let best: WorkerSlot | null = null;
    for (const slot of this.slots) {
      if (slot.dead) continue;
      if (best === null || slot.inFlight.size < best.inFlight.size) best = slot;
    }
    return best;
  }

  private onMessage(slot: WorkerSlot, msg: WorkerMessage): void {
    slot.inFlight.delete(msg.jobId);
    const job = this.jobs.get(msg.jobId);
    if (!job) return; // already settled by a crash, or the service was disposed
    this.jobs.delete(msg.jobId);

    if (msg.type === 'error') {
      const err = new Error(`mesh worker: ${msg.message}`);
      if (msg.stack) err.stack = `${err.stack ?? err.message}\n--- worker stack ---\n${msg.stack}`;
      job.reject(err);
      return;
    }

    try {
      job.resolve({
        geometry: buildGeometry(msg),
        jaw: msg.jaw,
        triangleCount: msg.triangleCount,
        solidVoxels: msg.solidVoxels,
        size: new THREE.Vector3(msg.size[0], msg.size[1], msg.size[2]),
        surface: {
          offsets: new Float32Array(msg.surfaceOffsets),
          colors: new Float32Array(msg.surfaceColors),
          count: msg.surfaceCount,
        },
        genome: job.genome,
        spec: job.spec,
        buildMs: performance.now() - job.startedAt,
        sculptMs: msg.sculptMs,
        meshMs: msg.meshMs,
      });
    } catch (cause) {
      job.reject(cause instanceof Error ? cause : new Error(String(cause)));
    }
  }

  /** Reject everything this worker was carrying, then replace it — a bounded number of times. */
  private killSlot(slot: WorkerSlot, message: string): void {
    if (slot.dead) return; // a terminated worker can still emit a trailing error event
    slot.dead = true;

    const err = new Error(message);
    for (const jobId of slot.inFlight) {
      const job = this.jobs.get(jobId);
      if (!job) continue;
      this.jobs.delete(jobId);
      job.reject(err);
    }
    slot.inFlight.clear();
    slot.worker.terminate();

    if (this.disposed || slot.restarts >= MAX_WORKER_RESTARTS) return;
    const replacement = this.spawn(slot.index);
    replacement.restarts = slot.restarts + 1;
    this.slots[slot.index] = replacement;
  }
}
